import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import {
  PhotoIcon
} from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';

const BrandFormModal = ({ isOpen, onClose, brand = null, onBrandCreated, onBrandUpdated }) => {
  const isEdit = !!brand;
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(brand?.logo || '');
  const [logoFile, setLogoFile] = useState(null);
  
  const [formData, setFormData] = useState({
    name: brand?.name || '',
    name_en: brand?.name_en || '',
    description: brand?.description || '',
    description_en: brand?.description_en || '',
    website: brand?.website || '',
    country: brand?.country || '',
    is_active: brand?.is_active !== undefined ? brand.is_active : true,
    is_featured: brand?.is_featured || false,
    sort_order: brand?.sort_order || 0,
    meta_title: brand?.meta_title || '',
    meta_description: brand?.meta_description || ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('גודל הקובץ חייב להיות קטן מ-2MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error('יש להעלות קובץ תמונה בלבד');
        return;
      }

      setLogoFile(file);
      
      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'שם הברנד הוא שדה חובה';
    }

    if (formData.website && !formData.website.match(/^https?:\/\/.+/)) {
      newErrors.website = 'כתובת האתר חייבת להתחיל ב-http:// או https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('נא למלא את כל השדות הנדרשים');
      return;
    }

    setLoading(true);

    try {
      let brandData = { ...formData };

      if (isEdit) {
        // Update brand
        const response = await axios.put(`/api/brands/${brand.id}`, brandData);
        
        // Upload logo if changed
        if (logoFile) {
          const logoFormData = new FormData();
          logoFormData.append('logo', logoFile);
          await axios.post(`/api/brands/${brand.id}/logo`, logoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }

        onBrandUpdated(response.data.brand);
      } else {
        // Create brand
        const response = await axios.post('/api/brands', brandData);
        
        // Upload logo if provided
        if (logoFile) {
          const logoFormData = new FormData();
          logoFormData.append('logo', logoFile);
          await axios.post(`/api/brands/${response.data.brand.id}/logo`, logoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }

        onBrandCreated(response.data.brand);
      }

      toast.success(isEdit ? 'הברנד עודכן בהצלחה' : 'הברנד נוסף בהצלחה');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בשמירת הברנד');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'עריכת ברנד' : 'ברנד חדש'}
      size="xl"
      preventClose={loading}
    >
      <form onSubmit={handleSubmit} className="space-y-6" dir="rtl">
        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            לוגו הברנד
          </label>
          
          <div className="flex items-center gap-4">
            {/* Preview */}
            <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300 overflow-hidden">
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="max-w-full max-h-full object-contain p-2"
                />
              ) : (
                <PhotoIcon className="h-12 w-12 text-gray-400" />
              )}
            </div>

            {/* Upload Button */}
            <div className="flex-1">
              <input
                type="file"
                id="logo-upload"
                accept="image/*"
                onChange={handleLogoChange}
                className="hidden"
              />
              <label
                htmlFor="logo-upload"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <PhotoIcon className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">
                  {logoPreview ? 'שנה לוגו' : 'העלה לוגו'}
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                PNG, JPG או GIF עד 2MB
              </p>
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name (Hebrew) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              שם הברנד (עברית) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="לדוגמה: נייקי"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Name (English) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              שם הברנד (אנגלית)
            </label>
            <input
              type="text"
              name="name_en"
              value={formData.name_en}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Example: Nike"
              dir="ltr"
            />
          </div>

          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              מדינת מוצא
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="לדוגמה: ארצות הברית"
            />
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              אתר רשמי
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.website ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="https://example.com"
              dir="ltr"
            />
            {errors.website && <p className="mt-1 text-sm text-red-600">{errors.website}</p>}
          </div>
        </div>

        {/* Description (Hebrew) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            תיאור הברנד (עברית)
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="תיאור קצר של הברנד..."
          />
        </div>

        {/* Description (English) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            תיאור הברנד (אנגלית)
          </label>
          <textarea
            name="description_en"
            value={formData.description_en}
            onChange={handleInputChange}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Short description of the brand..."
            dir="ltr"
          />
        </div>

        {/* SEO */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">SEO</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                כותרת מטא (Meta Title)
              </label>
              <input
                type="text"
                name="meta_title"
                value={formData.meta_title}
                onChange={handleInputChange}
                maxLength="60"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="כותרת למנועי חיפוש..."
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.meta_title.length}/60 תווים
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                תיאור מטא (Meta Description)
              </label>
              <textarea
                name="meta_description"
                value={formData.meta_description}
                onChange={handleInputChange}
                maxLength="160"
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="תיאור קצר למנועי חיפוש..."
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.meta_description.length}/160 תווים
              </p>
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">הגדרות</h3>
          
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="mr-2 text-sm text-gray-700">
                ברנד פעיל
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_featured"
                name="is_featured"
                checked={formData.is_featured}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_featured" className="mr-2 text-sm text-gray-700">
                ברנד מומלץ
              </label>
            </div>

            <div>
              <label htmlFor="sort_order" className="block text-sm font-medium text-gray-700 mb-1">
                סדר תצוגה
              </label>
              <input
                type="number"
                id="sort_order"
                name="sort_order"
                value={formData.sort_order}
                onChange={handleInputChange}
                min="0"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                מספר נמוך יוצג ראשון
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            ביטול
          </button>
          
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            )}
            <span>{isEdit ? 'עדכן' : 'צור'} ברנד</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default BrandFormModal;
